import type { BuildCookieOptions } from './types';
import {
  buildRawSetCookieHeader,
  readRawCookieFromDocument,
  readRawCookieValue,
  resolveBrowserCookieOptions,
} from './cookie';

/** Enter 自己维护的 Google Ads 点击 ID 首触 cookie。 */
export const ENTER_GCLID_COOKIE_NAME = 'enter_gclid';
/** Google Ads 自动标记可能写入的标准 cookie。 */
export const GOOGLE_ADS_GCLID_COOKIE_NAME = '_gcl_aw';

const GCLID_MAX_LENGTH = 512;

function normalizeGclid(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  if (
    !normalized ||
    new TextEncoder().encode(normalized).byteLength > GCLID_MAX_LENGTH
  ) {
    return null;
  }
  return normalized;
}

function decodeStoredGclid(rawValue: string | null): string | null {
  if (!rawValue) return null;

  try {
    return normalizeGclid(decodeURIComponent(rawValue));
  } catch {
    return null;
  }
}

/**
 * `_gcl_aw` 格式为 `GCL.<timestamp>.<gclid>`。
 * 点击 ID 理论上不含点，但这里保留第三段后的全部内容，避免过度截断。
 */
function parseGoogleAdsCookie(rawValue: string | null): string | null {
  if (!rawValue) return null;

  let decoded = rawValue;
  try {
    decoded = decodeURIComponent(rawValue);
  } catch {
    return null;
  }

  const segments = decoded.split('.');
  if (segments[0] !== 'GCL' || segments.length < 3) {
    return null;
  }
  return normalizeGclid(segments.slice(2).join('.'));
}

/** 从落地页 URL 读取本期唯一新增采集字段 gclid。 */
export function captureGclidFromUrl(
  searchParams: URLSearchParams,
): string | null {
  return normalizeGclid(searchParams.get('gclid'));
}

/**
 * 从请求 Cookie 中读取 gclid。
 * 优先使用 Google Ads 已有 `_gcl_aw`，否则回退到 Enter 的首触 cookie。
 */
export function readGclidFromCookieHeader(headers: Headers): string | null {
  const cookieHeader = headers.get('Cookie') ?? headers.get('cookie');
  const googleAdsGclid = parseGoogleAdsCookie(
    readRawCookieValue(cookieHeader, GOOGLE_ADS_GCLID_COOKIE_NAME),
  );
  if (googleAdsGclid) {
    return googleAdsGclid;
  }

  return decodeStoredGclid(
    readRawCookieValue(cookieHeader, ENTER_GCLID_COOKIE_NAME),
  );
}

/** 生成与 enter_utm 相同作用域和有效期策略的 gclid Set-Cookie 值。 */
export function buildGclidSetCookieHeader(
  gclid: string,
  options: BuildCookieOptions = {},
): string | null {
  const normalized = normalizeGclid(gclid);
  if (!normalized) {
    return null;
  }

  return buildRawSetCookieHeader(
    ENTER_GCLID_COOKIE_NAME,
    encodeURIComponent(normalized),
    options,
  );
}

/** 浏览器侧写入或清理 Enter 自有 gclid cookie。 */
export function writeGclidToDocument(
  gclid: string | null,
  options: BuildCookieOptions = resolveBrowserCookieOptions(),
): void {
  if (typeof document === 'undefined') return;

  try {
    if (gclid === null) {
      document.cookie = buildRawSetCookieHeader(ENTER_GCLID_COOKIE_NAME, '', {
        ...options,
        maxAge: 0,
      });
      return;
    }

    const setCookie = buildGclidSetCookieHeader(gclid, options);
    if (setCookie) {
      document.cookie = setCookie;
    }
  } catch {
    // Cookie 被浏览器禁用时保持 fail-open，不影响页面和登录请求。
  }
}

/** 浏览器侧读取 gclid，优先复用 `_gcl_aw`，再回退到 `enter_gclid`。 */
export function readGclidFromDocument(): string | null {
  const googleAdsGclid = parseGoogleAdsCookie(
    readRawCookieFromDocument(GOOGLE_ADS_GCLID_COOKIE_NAME),
  );
  if (googleAdsGclid) {
    return googleAdsGclid;
  }

  return decodeStoredGclid(readRawCookieFromDocument(ENTER_GCLID_COOKIE_NAME));
}

/**
 * 按 advertising consent 同步 Enter 自有 gclid：
 * 允许时从当前落地 URL 做首触持久化，拒绝或撤销时清理。
 */
export function syncGclidWithAdvertisingConsent(
  advertisingAllowed: boolean,
): void {
  if (typeof window === 'undefined') return;

  if (!advertisingAllowed) {
    writeGclidToDocument(null);
    return;
  }

  if (readGclidFromDocument()) {
    return;
  }

  const captured = captureGclidFromUrl(
    new URLSearchParams(window.location.search),
  );
  if (captured) {
    writeGclidToDocument(captured);
  }
}
